/**
 * Metadata the whole UI is generated from: navigation (per user, localised), every entity's
 * definition, module actions / queries and which entities print. Loaded once after sign-in and
 * again when the language changes.
 */
import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { api } from './api.ts';
import { useSession } from './session.tsx';
import type { ActionSpec, EntityMeta, NavModule, NavPage } from './types.ts';

export interface Meta {
  nav: NavModule[];
  entities: Map<string, EntityMeta>;
  actions: ActionSpec[];
  printable: Set<string>;
  /** longest entity route that prefixes the path → entity + remaining segments */
  resolve: (path: string) => { entity: EntityMeta; rest: string[] } | null;
  /** the navigation page (and module) for a path, by longest prefix */
  page: (path: string) => { module: NavModule; page: NavPage } | null;
  ready: boolean;
  error: string | null;
}

const MetaContext = createContext<Meta | null>(null);

export function MetaProvider({ children }: { children: ReactNode }) {
  const { session, locale } = useSession();
  const [state, setState] = useState<{ nav: NavModule[]; entities: EntityMeta[]; actions: ActionSpec[]; printable: string[]; key: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const key = session ? `${session.user.id}:${locale}` : '';

  useEffect(() => {
    if (!session) { setState(null); return; }
    let cancelled = false;
    setError(null);
    Promise.all([
      api.get<NavModule[]>('meta/navigation'),
      api.get<EntityMeta[]>('meta/entities'),
      api.get<ActionSpec[]>('meta/actions').catch(() => [] as ActionSpec[]),
      api.get<Array<{ entity: string }>>('print/templates').catch(() => [] as Array<{ entity: string }>),
    ]).then(([nav, entities, actions, printable]) => { if (!cancelled) setState({ nav, entities, actions, printable: printable.map((p) => p.entity), key }); })
      .catch((e: Error) => { if (!cancelled) setError(e.message); });
    return () => { cancelled = true; };
  }, [session, locale, key]);

  const value = useMemo<Meta>(() => {
    const entities = new Map((state?.entities ?? []).map((e) => [e.name, e]));
    const byRoute = [...entities.values()].sort((a, b) => b.route.length - a.route.length);
    const pages = (state?.nav ?? []).flatMap((m) => m.pages.map((p) => ({ module: m, page: p }))).sort((a, b) => b.page.path.length - a.page.path.length);
    const prefix = (path: string, route: string) => path === route || path.startsWith(`${route}/`);
    return {
      nav: state?.nav ?? [], entities, actions: state?.actions ?? [], printable: new Set(state?.printable ?? []),
      resolve: (path) => { const e = byRoute.find((x) => prefix(path, x.route)); return e ? { entity: e, rest: path.slice(e.route.length).split('/').filter(Boolean) } : null; },
      page: (path) => pages.find((x) => x.page.path === '/' ? path === '/' : prefix(path, x.page.path)) ?? null,
      ready: !!state && state.key === key, error,
    };
  }, [state, key, error]);

  return <MetaContext.Provider value={value}>{children}</MetaContext.Provider>;
}

export function useMeta(): Meta {
  const v = useContext(MetaContext);
  if (!v) throw new Error('useMeta outside MetaProvider');
  return v;
}

export function useEntity(name: string | null | undefined): EntityMeta | null {
  const { entities } = useMeta();
  return name ? entities.get(name) ?? null : null;
}
